// MyFeed.js — v17 FINAL (HTML Video + 100% Width Square + Full Autoplay + Swipe)

import React, { useEffect, useState, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  Animated,
  StatusBar,
} from "react-native";
import { useNavigation } from "@react-navigation/native";

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");
const MAX_CONTENT_WIDTH = SCREEN_WIDTH; // keep full width for square
const CONTENT_WIDTH = Math.min(SCREEN_WIDTH, MAX_CONTENT_WIDTH);
const CONTENT_HEIGHT = CONTENT_WIDTH; // 🔥 HEIGHT = WIDTH → perfect square

const isVideo = (url = "") =>
  /\.(mp4|mov|webm|m4v|avi)$/i.test(url?.toLowerCase() || "");

export default function MyFeed() {
  const navigation = useNavigation();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);

  // 🔊 SOUND ON BY DEFAULT
  const [soundEnabled, setSoundEnabled] = useState(true);

  const translateY = useRef(new Animated.Value(0)).current;
  const videoRef = useRef(null);

  let touchStartY = 0;
  let touchEndY = 0;



// ---------------------------
// TRACK FEED VIEW
// ---------------------------
const trackView = async (collabID) => {
  try {
    await fetch("https://collabbay.com/APIControllers/FeedContentView.php", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `CollabID=${collabID}`,
    });
  } catch (e) {
    console.log("View track error:", e);
  }
};


  // ---------------------------
  // FETCH FEED
  // ---------------------------
  const loadFeed = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        "https://collabbay.com/APIControllers/GetUserFeedContent.php"
      );
      const json = await res.json();
      if (json.status === "success" && Array.isArray(json.results)) {
        setFeed(json.results);
      }
    } catch (e) {
      console.log("Feed error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeed();
  }, []);

  // ---------------------------
  // SWIPE CONTROLS
  // ---------------------------
  const handleTouchStart = (e) => {
    if (!e.nativeEvent.touches?.[0]) return;
    touchStartY = e.nativeEvent.touches[0].clientY;
    touchEndY = touchStartY;
  };

  const handleTouchMove = (e) => {
    if (!e.nativeEvent.touches?.[0]) return;
    touchEndY = e.nativeEvent.touches[0].clientY;
    const delta = touchEndY - touchStartY;
    translateY.setValue(delta * 0.3);
  };

  const handleTouchEnd = () => {
    const delta = touchEndY - touchStartY;

    if (delta < -80) goToIndex(index + 1);
    else if (delta > 80) goToIndex(index - 1);
    else resetPosition();
  };

  const resetPosition = () =>
    Animated.spring(translateY, {
      toValue: 0,
      useNativeDriver: true,
    }).start();

  const goToIndex = (newIndex) => {
    if (newIndex < 0 || newIndex >= feed.length) return resetPosition();

Animated.timing(translateY, {
  toValue: newIndex > index ? -SCREEN_HEIGHT : SCREEN_HEIGHT,
  duration: 200,
  useNativeDriver: true,
}).start(() => {
  translateY.setValue(0);
  setIndex(newIndex);

  // AUTO SOUND ON
  setSoundEnabled(true);
// 🔥 TRACK VIEW HERE
const newItem = feed[newIndex];
if (newItem?.CollabID) {
  trackView(newItem.CollabID);
}

});

  };

  // ---------------------------
  // LOADING
  // ---------------------------
  if (loading) {
    return (
      <View style={styles.centerScreen}>
        <ActivityIndicator size="large" color="#fff" />
        <Text style={styles.msg}>Loading…</Text>
      </View>
    );
  }

  if (!feed.length) {
    return (
      <View style={styles.centerScreen}>
        <Text style={styles.msg}>No content</Text>
      </View>
    );
  }

  const item = feed[index];

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" />

<TouchableOpacity
  onPress={() => navigation.goBack()}
  style={{
    position: "absolute",
    top: 40,
    left: 20,
    zIndex: 999,
    padding: 10,
  }}
>
  <Text style={{ color: "#fff", fontSize: 26 }}>←</Text>
</TouchableOpacity>



      <Animated.View
        style={[styles.slideContainer, { transform: [{ translateY }] }]}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <MediaSlide
          item={item}
          soundEnabled={soundEnabled}
          setSoundEnabled={setSoundEnabled}
          videoRef={videoRef}
          navigation={navigation}
        />
      </Animated.View>
    </View>
  );
}

// ------------------------------------------------------
// MEDIA SLIDE
// ------------------------------------------------------
function MediaSlide({ item, soundEnabled, setSoundEnabled, videoRef, navigation }) {

  const trackClick = async () => {
    try {
      await fetch("https://collabbay.com/APIControllers/FeedContentClick.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: `CollabID=${item.CollabID}`,
      });
    } catch (e) {
      console.log("Click track error:", e);
    }
  };


  const { Image: mediaUrl, ProfileImage, Promoter, BrandName, ListingID } = item;
  const isVid = isVideo(mediaUrl);

  // re-autoplay on slide change
  useEffect(() => {
    if (videoRef.current && isVid) {
      setTimeout(() => {
        try {
          videoRef.current?.play();
        } catch {}
      }, 30);
    }
  }, [mediaUrl]);

  if (!mediaUrl) {
    return (
      <View style={styles.centerScreen}>
        <Text style={styles.msg}>Invalid media</Text>
      </View>
    );
  }

  return (
    <View style={styles.slide}>
      <TouchableOpacity
        activeOpacity={1}
        style={styles.videoContainer}
        onPress={() => setSoundEnabled((s) => !s)}
      >
        {isVid ? (
          <video
            ref={videoRef}
            src={mediaUrl}
            autoPlay
            loop
            playsInline
            defaultMuted
            muted={!soundEnabled}
            onLoadedMetadata={() => videoRef.current?.play()}
            onCanPlay={() => videoRef.current?.play()}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              backgroundColor: "#000",
            }}
          />
        ) : (
          <Image
            source={{ uri: mediaUrl }}
            style={styles.media}
            resizeMode="contain"
          />
        )}
      </TouchableOpacity>

      {/* Bottom Overlay */}
      <View style={styles.overlay}>
        <View style={styles.leftBlock}>
          <Image source={{ uri: ProfileImage }} style={styles.avatar} />
          <View>
            <Text style={styles.username}>{Promoter}</Text>
            {BrandName && <Text style={styles.brandTxt}>@ {BrandName}</Text>}
          </View>
        </View>

        {ListingID && (
<TouchableOpacity
  style={styles.cta}
onPress={() => {
  // 🔥 Track click
  trackClick();

  try {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.muted = true;
    }
  } catch {}

  navigation.navigate("CreateSubmissionPage", {
    ListingID,
    BrandName,
    Promoter,
  });
}}

>
  <Text style={styles.ctaTxt}>Book</Text>
</TouchableOpacity>

        )}
      </View>

      {/* Sound hint */}
      <View style={styles.soundHint}>
        <Text style={styles.soundHintText}>
          {soundEnabled ? "Tap to mute 🔊 Scroll to browse" : "Tap to unmute 🔈Scroll to browse"}
        </Text>
      </View>
    </View>
  );
}

// ------------------------------------------------------
// STYLES
// ------------------------------------------------------
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#000" },

  centerScreen: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#000",
  },

  msg: { color: "#fff", marginTop: 8 },

  slideContainer: {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
    justifyContent: "center",
    alignItems: "center",
  },

  slide: {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#000",
  },

  videoContainer: {
    width: CONTENT_WIDTH,
    height: SCREEN_HEIGHT, // square box
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    backgroundColor: "#000",
    position: "relative",
  },

  media: {
    width: "100%",
    height: "100%",
    backgroundColor: "#000",
    objectFit: "contain",
    objectPosition: "center center",
  },

  overlay: {
    position: "absolute",
    bottom: 40,
    left: 20,
    right: 20,
    flexDirection: "row",
    padding: 12,
    backgroundColor: "rgba(0,0,0,0.35)",
    borderRadius: 14,
    justifyContent: "space-between",
  },

  leftBlock: { flexDirection: "row", alignItems: "center", flex: 1 },

  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: "#fdf05d",
    marginRight: 12,
  },

  username: { color: "#fff", fontWeight: "700", fontSize: 15 },

  brandTxt: { color: "#fdf05d", fontSize: 13 },

  cta: {
    backgroundColor: "#fdf05d",
    paddingHorizontal: 16,
    paddingVertical: 5,
    borderRadius: 999,
  },

  ctaTxt: { color: "#000", fontWeight: "700" },

  soundHint: {
    position: "absolute",
    top: 40,
    backgroundColor: "rgba(0,0,0,0.5)",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
  },

  soundHintText: { color: "#fff", fontSize: 12 },
});

